import { API } from './api';
import { storedApiKey } from './apiKey';

const AGENT = 'http://127.0.0.1:9187';

// Print the labels on the thermal printer, silently, via the local agent.
//
// Returns null when the agent cannot be reached at all, which is the signal to
// fall back to opening the PDF — on any machine that is not the one with the
// printer attached, that fallback is the only thing that works.
async function printViaAgent(waybills) {
  try {
    // Ask first whether this machine can print silently at all. Without this
    // the agent accepts the job, fails on a missing SumatraPDF, and the labels
    // are neither printed nor shown — worse than never having tried.
    const ping = await fetch(`${AGENT}/ping`, { signal: AbortSignal.timeout(4000) });
    const state = await ping.json();
    if (!state.canPrintSilently) return null;

    const res = await fetch(`${AGENT}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ waybills }),
      signal: AbortSignal.timeout(60000)
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) throw new Error(body.error || `agent returned ${res.status}`);
    return { printed: body.printed ?? waybills.length, skipped: body.skipped || [] };
  } catch (e) {
    return null;
  }
}

// Get the labels onto paper. The agent prints them on the thermal printer with
// no dialog; if it is not running — or this is not the PC with the printer —
// the PDF opens in a tab instead, which is what used to happen every time.
// Shared by the Manifest and Orders tabs so a reprint behaves the same
// wherever it starts.
export async function printLabels(waybills) {
  const viaAgent = await printViaAgent(waybills);
  if (viaAgent) return { ...viaAgent, printedOnPrinter: true, popupBlocked: false };
  const opened = await openLabels(waybills);
  return { ...opened, printedOnPrinter: false };
}

// Fetch the merged label PDF and hand back a blob URL. Split out from
// openLabels so a caller can show the PDF inline instead of in a tab - the
// fallback when the browser blocks the popup.
export async function fetchLabelsPdf(waybills) {
  // The session lives in a cookie on another origin, so it only rides along
  // with credentials: 'include' — axios is configured for this globally, but
  // this is a raw fetch and has to say so itself. Without it the server saw an
  // anonymous request and 401'd, and the old key helper answered that by
  // prompting for a key that no longer exists once sign-in is on.
  const key = storedApiKey();
  const response = await fetch(`${API}/api/manifest/get-labels`, {
    method: 'POST',
    credentials: 'include',
    headers: Object.assign(
      { 'Content-Type': 'application/json' },
      key ? { 'x-api-key': key } : {}
    ),
    body: JSON.stringify({ waybills })
  });

  // Without this an error JSON body gets opened as though it were a PDF, so a
  // real failure looked like a blank or corrupt label instead of a message.
  if (!response.ok) {
    let message = `Label request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body && body.message) message = body.message;
    } catch (e) { /* not JSON — keep the status message */ }
    throw new Error(message);
  }

  // Delhivery may not have generated a just-booked label yet; those come back
  // in a header rather than failing the whole batch.
  const skipped = (response.headers.get('x-skipped-waybills') || '').split(',').filter(Boolean);

  const blob = await response.blob();
  return { blob, url: URL.createObjectURL(blob), skipped };
}

// Ask Chrome's own PDF viewer to print the tab we just opened.
//
// The window 'load' event is unreliable for the built-in PDF plugin - it
// sometimes never fires - so a timer backs it up, and a flag keeps the two
// paths from both firing and showing the print dialog twice. This opens
// Chrome's normal print dialog: it is NOT silent, unlike the SumatraPDF
// agent, so the user picks the printer and presses Print.
function printWhenReady(win) {
  let done = false;
  const go = () => {
    if (done || win.closed) return;
    done = true;
    try { win.focus(); win.print(); } catch (e) { /* user can still press Ctrl+P */ }
  };
  try { win.addEventListener('load', go); } catch (e) { /* cross-origin guard */ }
  setTimeout(go, 1500);
}

// Open the label PDF in a new tab. Pass { print: true } to also raise Chrome's
// print dialog on that tab once the viewer has loaded.
//
// Returns the blob url so a caller can fall back to an inline viewer when the
// popup was blocked; in that case the url is NOT revoked here, because the
// caller still needs it.
export async function openLabels(waybills, { print = false } = {}) {
  const { url, skipped } = await fetchLabelsPdf(waybills);
  const win = window.open(url, '_blank');
  if (win && print) printWhenReady(win);
  if (win) setTimeout(() => URL.revokeObjectURL(url), 60000);
  return { skipped, popupBlocked: !win, url, win };
}

// Every distinct waybill on an order. A split order carries one per parcel on
// the items, and only the first also sits at order level — so reading just
// order.delhiveryWaybill would miss the later parcels.
export function waybillsForOrder(order) {
  const seen = [];
  const add = (w) => {
    const s = String(w || '').trim();
    if (s && !seen.includes(s)) seen.push(s);
  };
  add(order.delhiveryWaybill);
  (order.items || []).forEach(i => add(i.waybill));
  return seen;
}
